import { handleError } from "../../../utils/helpers";
import {
  EarningsResponse,
  EarningsSummaryResponse,
  EventGuestsResponse,
  GetOrganizersEventsResponse,
  GetSalesEvents,
  GetSalesInvitedOrganizers,
  MonthlyEarningsResponse,
  QueryParams,
  SalesAgentPerformance,
  SalesDashboardMetrics,
} from "../../../utils/types";
import { salesBaseApi } from "../../api";
import { GetEventResponse } from "../event/event.types";

export const salesApi = salesBaseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getSalesEvents: builder.query<GetSalesEvents, QueryParams>({
      query: (params) => {
        return {
          url: `sales/events-table`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSalesInvitedOrganizers: builder.query<
      GetSalesInvitedOrganizers,
      QueryParams
    >({
      query: (params) => {
        return {
          url: `sales/invited-organizers`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getDashboardMetrics: builder.query<SalesDashboardMetrics, void>({
      query: () => {
        return {
          url: `sales/dashboard-metrics`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getEarningsSummary: builder.query<EarningsSummaryResponse, void>({
      query: () => {
        return {
          url: `sales/earnings-summary`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getMonthlyCommissions: builder.query<
      MonthlyEarningsResponse,
      { year: number }
    >({
      query: (params) => {
        return {
          url: `sales/monthly-earnings`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSalesGuest: builder.query<
      EventGuestsResponse,
      QueryParams & { id?: string }
    >({
      query: (params) => {
        const { id, ...queryParams } = params;
        return {
          url: `sales/events/${id}/guests`,
          method: "GET",
          params: queryParams,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getAgentPerformance: builder.query<SalesAgentPerformance, { id?: string }>({
      query: (params) => {
        const { id } = params;
        return {
          url: `sales/events/${id}/agent-performance`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSingleEventBySlug: builder.query<GetEventResponse, { slug: string }>({
      query: ({ slug }) => {
        return {
          url: `events/${encodeURIComponent(slug)}/details`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getOrganizersEvents: builder.query<
      GetOrganizersEventsResponse,
      QueryParams & { id?: string }
    >({
      query: (params) => {
        const { id, ...queryParams } = params;
        return {
          url: `sales/users/${id}/events`,
          method: "GET",
          params: queryParams,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSalesEarnings: builder.query<
      EarningsResponse,
      QueryParams & { year: number }
    >({
      query: (params) => {
        return {
          url: `sales/monthly-earnings`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
  }),
});

export const {
  useGetSalesEventsQuery,
  useGetSalesInvitedOrganizersQuery,
  useGetDashboardMetricsQuery,
  useGetEarningsSummaryQuery,
  useGetMonthlyCommissionsQuery,
  useGetSalesGuestQuery,
  useGetAgentPerformanceQuery,
  useGetSingleEventBySlugQuery,
  useGetOrganizersEventsQuery,
  useGetSalesEarningsQuery,
} = salesApi;
